import React, { useEffect, useState, useRef } from 'react';
import './styles/tutorial.css';

/**
 * Composant pour afficher une étape individuelle du tutoriel
 * @param {Object} step - L'étape du tutoriel à afficher
 * @param {Function} onNext - Callback pour passer à l'étape suivante
 * @param {Function} onPrevious - Callback pour revenir à l'étape précédente
 * @param {Function} onSkip - Callback pour passer le tutoriel
 * @param {number} currentStep - Index de l'étape actuelle
 * @param {number} totalSteps - Nombre total d'étapes
 */
const TutorialStep = ({ step, onNext, onPrevious, onSkip, currentStep, totalSteps }) => {
    const [position, setPosition] = useState({ top: '50%', left: '50%' });
    const [arrowPosition, setArrowPosition] = useState('none');
    const bubbleRef = useRef(null);

    useEffect(() => {
        if (!step.target) {
            // Message centré
            setPosition({ top: '50%', left: '50%' });
            setArrowPosition('none');
            return;
        }

        // Trouver l'élément cible
        const targetElement = document.querySelector(step.target);
        if (!targetElement) {
            console.warn(`Élément cible non trouvé: ${step.target}`);
            setPosition({ top: '50%', left: '50%' });
            setArrowPosition('none');
            return;
        }

        // Calculer la position de la bulle par rapport à l'élément cible
        const targetRect = targetElement.getBoundingClientRect();
        const bubbleWidth = 350;
        const bubbleHeight = 200;
        const offset = 20;

        let newPosition = {};
        let newArrowPosition = step.position || 'top';

        switch (step.position) {
            case 'top':
                newPosition = {
                    top: `${targetRect.top - bubbleHeight - offset}px`,
                    left: `${targetRect.left + targetRect.width / 2 - bubbleWidth / 2}px`
                };
                break;
            case 'bottom':
                newPosition = {
                    top: `${targetRect.bottom + offset}px`,
                    left: `${targetRect.left + targetRect.width / 2 - bubbleWidth / 2}px`
                };
                break;
            case 'left':
                newPosition = {
                    top: `${targetRect.top + targetRect.height / 2 - bubbleHeight / 2}px`,
                    left: `${targetRect.left - bubbleWidth - offset}px`
                };
                break;
            case 'right':
                newPosition = {
                    top: `${targetRect.top + targetRect.height / 2 - bubbleHeight / 2}px`,
                    left: `${targetRect.right + offset}px`
                };
                break;
            default:
                newPosition = {
                    top: '50%',
                    left: '50%'
                };
                newArrowPosition = 'none';
        }

        setPosition(newPosition);
        setArrowPosition(newArrowPosition);
    }, [step]);

    const isFirstStep = currentStep === 0;
    const isLastStep = currentStep === totalSteps - 1;

    return (
        <div
            ref={bubbleRef}
            className={`tutorial-bubble tutorial-bubble-${arrowPosition} ${step.position === 'center' ? 'tutorial-bubble-center' : ''
                }`}
            style={step.position === 'center' ? {} : position}
        >
            <div className="tutorial-bubble-header">
                <h3 className="tutorial-bubble-title">{step.title}</h3>
                <button
                    onClick={onSkip}
                    className="tutorial-bubble-close"
                    aria-label="Fermer le tutoriel"
                >
                    ✕
                </button>
            </div>

            <div className="tutorial-bubble-content">
                <p className="tutorial-bubble-description">{step.description}</p>
            </div>

            <div className="tutorial-bubble-footer">
                <div className="tutorial-bubble-progress">
                    {currentStep + 1} / {totalSteps}
                </div>
                <div className="tutorial-bubble-actions">
                    {!isFirstStep && (
                        <button
                            onClick={onPrevious}
                            className="tutorial-button tutorial-button-secondary"
                        >
                            Précédent
                        </button>
                    )}
                    <button
                        onClick={onSkip}
                        className="tutorial-button tutorial-button-ghost"
                    >
                        Passer
                    </button>
                    <button
                        onClick={onNext}
                        className="tutorial-button tutorial-button-primary"
                    >
                        {isLastStep ? 'Terminer' : 'Suivant'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default TutorialStep;
